// src/pages/Seller/Products.jsx
// ✅ Rewritten to match Admin AddFruit form exactly:
//    - Chip-based multi-value inputs for varieties, grades, counts, origins,
//      sizes, containerTypes, ports, certifications
//    - Same renderSelectionField pattern, same suggestions, same icons
//    - Array fields submitted as JSON.stringify (matches backend expectation)

import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, Tooltip, LinearProgress,
  Switch, FormControl, InputLabel, Select, MenuItem, Alert,
  Divider, alpha,
} from '@mui/material';
import {
  Search, Add, Edit, Delete, Refresh, Inventory,
  CloudUpload, Save, Cancel, CheckCircle, ClearAll, Info,
  LocalOffer, Star, Numbers, Flag, CropSquare, LocalShipping, Language, Grading,
} from '@mui/icons-material';
import { sellerAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CATEGORIES = [
  'Apple', 'Mango', 'Banana', 'Orange', 'Grapes', 'Pineapple',
  'Berries', 'Melon', 'Citrus', 'Tropical', 'Exotic', 'Other',
];
const MOQ_OPTIONS = [
  '1 container', '2 containers', '3 containers', '4 containers',
  '5 containers', '10 containers', 'Full Load',
];
const SEASONALITY_OPTIONS = [
  { value: 'year-round', label: 'Year-round' },
  { value: 'summer',     label: 'Summer' },
  { value: 'winter',     label: 'Winter' },
  { value: 'spring',     label: 'Spring' },
  { value: 'autumn',     label: 'Autumn' },
  { value: 'monsoon',    label: 'Monsoon' },
];

const ARRAY_FIELDS = ['varieties', 'grades', 'counts', 'origins', 'sizes', 'containerTypes', 'ports', 'certifications'];

const EMPTY_FORM = {
  name: '', category: '',
  varieties: [], grades: [], counts: [], origins: [],
  sizes: [], containerTypes: [], ports: [], certifications: [],
  moq: '1 container', price_per_unit: '',
  description: '', active: true,
  seasonality: 'year-round', taste_profile: '',
  nutritional_info: '', storage_requirements: '',
  shelf_life: '', hs_code: '',
};

const EMPTY_INPUTS = {
  varieties: '', grades: '', counts: '', origins: '',
  sizes: '', containerTypes: '', ports: '', certifications: '',
};

const SUGGESTIONS = {
  varieties:      ['Fuji', 'Gala', 'Kesar', 'Alphonso', 'Cavendish', 'Thompson Seedless', 'Granny Smith'],
  grades:         ['A', 'B', 'C', 'Premium', 'Standard', 'Export Quality'],
  counts:         ['80-100', '100-120', '120-140', '140-160', '160-180', '180-200'],
  sizes:          ['Small', 'Medium', 'Large', 'Extra Large', 'Jumbo', 'Mixed'],
  containerTypes: ['20 FT Reefer Container', '40 FT Reefer Container', '20 FT Dry Container', '40 FT Dry Container'],
  certifications: ['Organic', 'ISO 9001', 'HACCP', 'GlobalG.A.P.', 'Fair Trade', 'FSSAI', 'APEDA'],
  origins:        ['India', 'USA', 'China', 'Brazil', 'Australia', 'South Africa', 'Chile'],
  ports:          ['Chennai (IN MAA)', 'Mumbai (IN BOM)', 'Singapore (SG SIN)', 'Dubai (AE DXB)', 'Rotterdam (NL RTM)'],
};

const FIELD_ICONS = {
  varieties:      <LocalOffer fontSize="small" />,
  grades:         <Star fontSize="small" />,
  counts:         <Numbers fontSize="small" />,
  origins:        <Flag fontSize="small" />,
  sizes:          <CropSquare fontSize="small" />,
  containerTypes: <LocalShipping fontSize="small" />,
  ports:          <Language fontSize="small" />,
  certifications: <Grading fontSize="small" />,
};
const FIELD_LABELS = {
  varieties:      'Varieties',
  grades:         'Grades',
  counts:         'Counts',
  origins:        'Origin Countries',
  sizes:          'Sizes',
  containerTypes: 'Container Types',
  ports:          'Ports of Discharge',
  certifications: 'Certifications',
};
const FIELD_DESCRIPTIONS = {
  varieties:      'Enter fruit varieties (e.g., Fuji, Kesar)',
  grades:         'Enter quality grades (e.g., A, Premium)',
  counts:         'Enter count ranges (e.g., 80-100)',
  origins:        'Enter origin countries (e.g., India, USA)',
  sizes:          'Enter size categories (e.g., Small, Large)',
  containerTypes: 'Enter container types (e.g., 20 FT Reefer)',
  ports:          'Enter discharge port names',
  certifications: 'Enter certifications (e.g., Organic, FSSAI)',
};

// ─────────────────────────────────────────────────────────────────────────────

const SellerProducts = () => {
  const [fruits,       setFruits]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [dialog,       setDialog]       = useState(null);   // null | 'add' | 'edit'
  const [formData,     setFormData]     = useState(EMPTY_FORM);
  const [inputValues,  setInputValues]  = useState(EMPTY_INPUTS);
  const [editId,       setEditId]       = useState(null);
  const [imageFile,    setImageFile]    = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchFruits(); }, []);

  const fetchFruits = async () => {
    setLoading(true);
    try {
      const res  = await sellerAPI.getMyFruits();
      const data = res.data;
      setFruits(Array.isArray(data) ? data : (data.fruits || []));
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // ── helpers to parse arrays stored as JSON strings from backend ─────────────
  const parseArr = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.startsWith('[')) {
      try { return JSON.parse(val); } catch { /* fall through */ }
    }
    if (typeof val === 'string' && val.length) return [val];
    return [];
  };

  // ── open dialogs ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setInputValues(EMPTY_INPUTS);
    setEditId(null);
    setImageFile(null);
    setDialog('add');
  };

  const openEdit = (f) => {
    setFormData({
      name:                f.name          || '',
      category:            f.category      || '',
      varieties:           parseArr(f.varieties),
      grades:              parseArr(f.grades),
      counts:              parseArr(f.counts),
      origins:             parseArr(f.origins),
      sizes:               parseArr(f.sizes),
      containerTypes:      parseArr(f.containerTypes || f.container_types),
      ports:               parseArr(f.ports),
      certifications:      parseArr(f.certifications),
      moq:                 f.moq || f.min_order || '1 container',
      price_per_unit:      f.price_per_unit || f.price || '',
      description:         f.description   || '',
      active:              f.active !== false,
      seasonality:         f.seasonality   || 'year-round',
      taste_profile:       f.taste_profile || '',
      nutritional_info:    f.nutritional_info || '',
      storage_requirements:f.storage_requirements || '',
      shelf_life:          f.shelf_life    || '',
      hs_code:             f.hs_code       || '',
    });
    setInputValues(EMPTY_INPUTS);
    setEditId(f.id);
    setImageFile(null);
    setDialog('edit');
  };

  // ── chip-array handlers ──────────────────────────────────────────────────────
  const handleInputChange = (field, value) => {
    setInputValues(prev => ({ ...prev, [field]: value }));
  };

  const handleAddItem = (field, value) => {
    const trimmed = value.trim();
    if (!trimmed) { toast.error(`Please enter a ${FIELD_LABELS[field].toLowerCase()}`); return; }
    if (formData[field].includes(trimmed)) { toast.error('This value already exists'); return; }
    setFormData(prev => ({ ...prev, [field]: [...prev[field], trimmed] }));
    setInputValues(prev => ({ ...prev, [field]: '' }));
  };

  const handleRemoveItem = (field, item) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter(i => i !== item) }));
  };

  const handleClearAll = (field) => {
    setFormData(prev => ({ ...prev, [field]: [] }));
  };

  const handleKeyPress = (field, e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = inputValues[field].trim();
      if (val) handleAddItem(field, val.replace(',', ''));
    }
  };

  // ── save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Name and category are required'); return;
    }
    if (formData.varieties.length === 0) { toast.error('Add at least one variety'); return; }
    if (formData.grades.length === 0)    { toast.error('Add at least one grade');   return; }
    if (formData.origins.length === 0)   { toast.error('Add at least one origin country'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (ARRAY_FIELDS.includes(k)) {
          fd.append(k, JSON.stringify(v));
        } else {
          fd.append(k, v);
        }
      });
      if (imageFile) fd.append('image', imageFile);

      if (dialog === 'add') {
        await sellerAPI.addFruit(fd);
        toast.success('Product added!');
      } else {
        await sellerAPI.updateFruit(editId, fd);
        toast.success('Product updated!');
      }
      setDialog(null);
      fetchFruits();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await sellerAPI.deleteFruit(deleteTarget.id);
      toast.success('Product deleted');
      setDeleteTarget(null);
      fetchFruits();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ── renderSelectionField (identical pattern to Admin AddFruit) ───────────────
  const renderSelectionField = (field, required = false) => {
    const items      = formData[field] || [];
    const inputValue = inputValues[field] || '';
    const suggList   = SUGGESTIONS[field]  || [];

    return (
      <Grid item xs={12} md={6} key={field}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {FIELD_ICONS[field]}
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {FIELD_LABELS[field]}{required && ' *'}
                </Typography>
              </Box>
              {items.length > 0 && (
                <Tooltip title="Clear all">
                  <IconButton size="small" onClick={() => handleClearAll(field)} color="error">
                    <ClearAll />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField fullWidth size="small"
                placeholder={`Add ${FIELD_LABELS[field].toLowerCase()}...`}
                value={inputValue}
                onChange={(e) => handleInputChange(field, e.target.value)}
                onKeyPress={(e) => handleKeyPress(field, e)}
                InputProps={{
                  endAdornment: inputValue && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => handleAddItem(field, inputValue)} color="primary">
                        <Add />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                {FIELD_DESCRIPTIONS[field]}. Press Enter to add.
              </Typography>
            </Box>

            {suggList.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  Quick add:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {suggList.slice(0, 6).map((s, i) => (
                    <Chip key={i} label={s} size="small"
                      onClick={() => handleAddItem(field, s)}
                      variant="outlined" sx={{ cursor: 'pointer', fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>
            )}

            {items.length > 0 ? (
              <Box sx={{
                p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                maxHeight: 120, overflowY: 'auto', bgcolor: alpha('#1976d2', 0.03),
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {items.map((item, i) => (
                    <Chip key={i} label={item} onDelete={() => handleRemoveItem(field, item)}
                      color="primary" variant="filled" size="small"
                      sx={{ '& .MuiChip-deleteIcon': { color: 'white' } }} />
                  ))}
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  {items.length} item(s)
                </Typography>
              </Box>
            ) : (
              <Alert severity="info" icon={<Info />}
                sx={{ bgcolor: 'background.default', border: '1px dashed', borderColor: 'info.main' }}>
                No {FIELD_LABELS[field].toLowerCase()} added yet.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // ── display helpers ──────────────────────────────────────────────────────────
  const displayArr = (val) => {
    const arr = parseArr(val);
    if (!arr.length) return '—';
    return arr.length === 1 ? arr[0] : `${arr[0]} +${arr.length - 1}`;
  };

  const filtered = fruits.filter(f =>
    (f.name     || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (f.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const activeCount   = fruits.filter(f => f.active !== false).length;
  const inactiveCount = fruits.length - activeCount;

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography color="text.secondary">Loading products...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">My Products</Typography>
          <Typography color="text.secondary">Manage your fruit listings</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchFruits}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Product</Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total',    value: fruits.length, color: '#2196f3', icon: <Inventory /> },
          { label: 'Active',   value: activeCount,   color: '#4caf50', icon: <CheckCircle /> },
          { label: 'Inactive', value: inactiveCount, color: '#ff9800', icon: <Cancel /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold">{s.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth size="small"
          placeholder="Search products..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['Product', 'Category', 'Varieties', 'Grades', 'Price / Unit', 'MOQ', 'Origins', 'Status', 'Actions'].map(h => (
                  <TableCell key={h}>
                    <Typography variant="subtitle2" fontWeight="bold">{h}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Inventory sx={{ fontSize: 64, color: 'text.disabled', display: 'block', mx: 'auto', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No products yet</Typography>
                    <Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }} onClick={openAdd}>
                      Add Your First Product
                    </Button>
                  </TableCell>
                </TableRow>
              ) : filtered.map(f => (
                <TableRow key={f.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar variant="rounded" sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>
                        {(f.name || 'F').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{f.name}</Typography>
                        <Typography variant="caption" color="text.secondary">ID: {f.id}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={f.category} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Typography variant="body2">{displayArr(f.varieties)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{displayArr(f.grades)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      ${parseFloat(f.price_per_unit || f.price || 0).toFixed(2)} / unit
                    </Typography>
                  </TableCell>
                  <TableCell>{f.moq || f.min_order || '1 container'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{displayArr(f.origins)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={f.active !== false ? 'Active' : 'Inactive'} size="small"
                      color={f.active !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(f)}><Edit /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(f)}><Delete /></IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} of {fruits.length} products
          </Typography>
        </Box>
      </Paper>

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle fontWeight="bold">
          {dialog === 'add' ? '➕ Add New Product' : '✏️ Edit Product'}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>

            {/* ── Basic Info ── */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main',
                pb: 0.5, display: 'inline-block',
              }}>
                Basic Information
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fruit Name *" size="small"
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Category *</InputLabel>
                <Select label="Category *" value={formData.category}
                  onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}>
                  <MenuItem value=""><em>Select Category</em></MenuItem>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" size="small" multiline rows={3}
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} />
            </Grid>

            {/* ── Product Specifications (chip-array) ── */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main',
                pb: 0.5, display: 'inline-block',
              }}>
                Product Specifications
              </Typography>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                {renderSelectionField('varieties', true)}
                {renderSelectionField('grades', true)}
                {renderSelectionField('origins', true)}
                {renderSelectionField('sizes')}
                {renderSelectionField('counts')}
                {renderSelectionField('certifications')}
                {renderSelectionField('containerTypes')}
                {renderSelectionField('ports')}
              </Grid>
            </Grid>

            {/* ── Pricing & Order ── */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main',
                pb: 0.5, display: 'inline-block',
              }}>
                Pricing &amp; Order
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Price per Unit (USD)" size="small" type="number"
                value={formData.price_per_unit}
                onChange={e => setFormData(p => ({ ...p, price_per_unit: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                helperText="Leave 0 for 'Request for Quote'" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Minimum Order Quantity</InputLabel>
                <Select label="Minimum Order Quantity" value={formData.moq}
                  onChange={e => setFormData(p => ({ ...p, moq: e.target.value }))}>
                  {MOQ_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="HS Code (Harmonised System)" size="small"
                placeholder="e.g. 0804.50 (Mango)"
                value={formData.hs_code}
                onChange={e => setFormData(p => ({ ...p, hs_code: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Seasonality / Availability</InputLabel>
                <Select label="Seasonality / Availability" value={formData.seasonality}
                  onChange={e => setFormData(p => ({ ...p, seasonality: e.target.value }))}>
                  {SEASONALITY_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Additional Details ── */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main',
                pb: 0.5, display: 'inline-block',
              }}>
                Additional Details
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Taste Profile" size="small"
                placeholder="e.g., Sweet, Tangy, Juicy"
                value={formData.taste_profile}
                onChange={e => setFormData(p => ({ ...p, taste_profile: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Shelf Life" size="small"
                placeholder="e.g., 30 days at 2°C"
                value={formData.shelf_life}
                onChange={e => setFormData(p => ({ ...p, shelf_life: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Storage Requirements" size="small"
                placeholder="e.g., Cold storage 2–4°C"
                value={formData.storage_requirements}
                onChange={e => setFormData(p => ({ ...p, storage_requirements: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nutritional Info" size="small"
                placeholder="e.g., Rich in Vitamin C"
                value={formData.nutritional_info}
                onChange={e => setFormData(p => ({ ...p, nutritional_info: e.target.value }))} />
            </Grid>

            {/* ── Status & Image ── */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">Listing Status:</Typography>
                <Switch checked={formData.active} color="success"
                  onChange={e => setFormData(p => ({ ...p, active: e.target.checked }))} />
                <Typography variant="body2" color={formData.active ? 'success.main' : 'text.secondary'}>
                  {formData.active ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />} fullWidth>
                {imageFile ? `📷 ${imageFile.name}` : 'Upload Product Image (optional)'}
                <input type="file" hidden accept="image/*"
                  onChange={e => setImageFile(e.target.files[0])} />
              </Button>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialog(null)} startIcon={<Cancel />}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={<Save />}>
            {saving ? 'Saving...' : 'Save Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default SellerProducts;
